import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import api from '../services/api';
import { canUseViewAs } from '../utils/permissions';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import SideNavLayout from './SideNavLayout';
import './MainLayout.css';

/* Icons for main left nav (24x24 outline, stroke currentColor) */
const NavIcon = ({ children }) => <span className="side-nav-link-icon" aria-hidden="true">{children}</span>;
const IconDashboard = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg></NavIcon>);
const IconDocuments = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></NavIcon>);
const IconReports = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></NavIcon>);
const IconClients = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></NavIcon>);
const IconChecklist = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg></NavIcon>);
const IconServiceNeeds = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg></NavIcon>);
const IconAtPackets = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></NavIcon>);
const IconFileReviews = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></NavIcon>);
const IconTraining = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></NavIcon>);
const IconTransfers = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg></NavIcon>);
const IconViewAs = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg></NavIcon>);
const IconSuperAdmin = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></NavIcon>);
const IconMonthlyContact = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></NavIcon>);
const IconCornerstone = () => (<NavIcon><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="11" height="16" rx="1"/><line x1="7" y1="8" x2="10" y2="8"/><line x1="7" y1="11" x2="10" y2="11"/><line x1="7" y1="14" x2="10" y2="14"/><polyline points="14 10 17 13 22 8"/></svg></NavIcon>);
/** Outline clipboard + list (distinct from checklist icon) */
const IconMyTodos = () => (
  <NavIcon>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="12" y2="16" />
    </svg>
  </NavIcon>
);

const ICONS = {
  dashboard: IconDashboard,
  documents: IconDocuments,
  reports: IconReports,
  clients: IconClients,
  'my-checklists': IconChecklist,
  'client-services': IconServiceNeeds,
  'my-todos': IconMyTodos,
  'monthly-contact': IconMonthlyContact,
  'incomplete-to-cornerstone': IconCornerstone,
  'at-packets': IconAtPackets,
  'file-reviews': IconFileReviews,
  training: IconTraining,
  transfers: IconTransfers,
  'view-as': IconViewAs,
  'super-admin': IconSuperAdmin,
};

function getListsPath(segment, tenantPath) {
  const rel = `/lists/${segment}`;
  return tenantPath ? tenantPath(rel) : rel;
}

/**
 * Unified left nav (menu): Dashboard, Lists, Documents, Reports at top;
 * then list items (My Clients, My Checklists, etc.); View as / Super Admin at bottom.
 * Used for dashboard, lists, documents, reports, view-as so the menu stays visible.
 */
function MainLayout() {
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location.pathname;

  const [showChecklist, setShowChecklist] = useState(false);
  const [showClientServices, setShowClientServices] = useState(false);
  const [showMonthlyContact, setShowMonthlyContact] = useState(false);
  const [showClientWorkTasks, setShowClientWorkTasks] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [whoamiPermissions, setWhoamiPermissions] = useState([]);

  const fetchWhoami = useCallback(() => {
    api.get('/whoami')
      .then((res) => {
        const permissions = res.data?.permissions || [];
        setWhoamiPermissions(permissions);
        setShowChecklist(res.data?.features?.checklist === true);
        setShowClientServices(res.data?.features?.client_services_provider_search === true);
        setShowMonthlyContact(res.data?.features?.monthly_contact === true);
        setShowClientWorkTasks(res.data?.features?.client_work_tasks !== false);
        setIsSuperAdmin(res.data?.is_super_admin === true);
      })
      .catch(() => {
        setWhoamiPermissions([]);
        setShowChecklist(false);
        setShowClientServices(false);
        setShowMonthlyContact(false);
        setShowClientWorkTasks(false);
        setIsSuperAdmin(false);
      });
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  const has = (code) => whoamiPermissions.includes(code);

  // Left nav: My Clients plus workflow items. Directory + org-wide lists (Providers, Payees, AT Packets, File Reviews, etc.) live in top-nav Lists dropdown.
  // Left nav: "my" workflow only (My Clients, My Checklists, My Service Needs, Monthly Contact). Org-wide tasks (SC Assignment, New Client/Referral, Data Corrections, etc.) live in top-nav Lists dropdown.
  const listItems = [
    { id: 'clients', name: 'My Clients' },
    ...(showChecklist && has('checklists.view') ? [{ id: 'my-checklists', name: 'My Checklists' }] : []),
    ...(showClientServices && (has('client_services.manage_needs') || has('client_services.search_providers')) ? [{ id: 'client-services', name: 'My Service Needs' }] : []),
    ...(showClientWorkTasks && has('client_work_tasks.read') ? [{ id: 'my-todos', name: 'My To-Dos' }] : []),
    ...(showMonthlyContact && has('work_tasks.view') ? [{ id: 'monthly-contact', name: 'Monthly Contact' }] : []),
  ];

  const isListActive = (id) => {
    const base = pathname.replace(/\/$/, '');
    const target = getListsPath(id, tenantPath).replace(/\/$/, '');
    return base === target || base.startsWith(target + '/');
  };

  const isMainActive = (path) => {
    const base = pathname.replace(/\/$/, '');
    const target = (tenantPath ? tenantPath(path) : path).replace(/\/$/, '');
    if (path === '/lists') return base === target || base.startsWith(target + '/');
    return base === target || base.startsWith(target + '/');
  };

  const NavLink = ({ to, active, icon: Icon, title, children }) => (
    <Link to={to} className={`side-nav-link ${active ? 'active' : ''}`} title={title || children}>
      {Icon && <Icon />}
      <span className="side-nav-link-text">{children}</span>
    </Link>
  );

  const nav = (
    <div className="main-layout-nav-inner">
      <div className="main-layout-section main-layout-section-top">
        <NavLink
          to={tenantPath('/dashboard')}
          active={isMainActive('/dashboard') || isMainActive('/work-tasks')}
          icon={ICONS.dashboard}
          title="Dashboard"
        >
          Dashboard
        </NavLink>
        <NavLink to={tenantPath('/documents')} active={isMainActive('/documents')} icon={ICONS.documents} title="Document generation">Doc Gen</NavLink>
        <NavLink to={tenantPath('/reports')} active={isMainActive('/reports')} icon={ICONS.reports} title="Reports">Reports</NavLink>
      </div>
      <div className="main-layout-section main-layout-section-lists">
        {listItems.map((item) => {
          const Icon = ICONS[item.id];
          return (
            <Link
              key={item.id}
              to={getListsPath(item.id, tenantPath)}
              className={`side-nav-link ${isListActive(item.id) ? 'active' : ''}`}
              title={item.name}
            >
              {Icon && <Icon />}
              <span className="side-nav-link-text">{item.name}</span>
            </Link>
          );
        })}
      </div>
      {(canUseViewAs() || isSuperAdmin) && (
        <div className="main-layout-section main-layout-section-footer">
          <div className="main-layout-section-label">Admin</div>
          {canUseViewAs() && (
            <NavLink to={tenantPath('/view-as')} active={isMainActive('/view-as')} icon={ICONS['view-as']} title="View as">View as</NavLink>
          )}
          {isSuperAdmin && (
            <NavLink to={tenantPath('/super-admin')} active={isMainActive('/super-admin')} icon={ICONS['super-admin']} title="Super Admin">Super Admin</NavLink>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SideNavLayout nav={nav} navClassName="side-nav-layout-nav--main">
      <Outlet />
    </SideNavLayout>
  );
}

export default MainLayout;
